<!--
 * @Description: 附件预览弹窗
 * @Version: 1.0
 * @Autor: 胡彩丽 hucaili@qfei.cn
 * @LastEditors: 胡彩丽 hucaili@qfei.cn
-->
<template>
  <jw-modal v-model:visible="visible" width="100%" height="100%" :footer="false" :mask-closable="false" class="preview-modal">
    <template #title>
      <div class="preview-modal-title">
        <icon-upload :activeFile="getFileItem" />
        <jw-tooltip position="bl" :content="getFileItem?.name || $t('无命名文件')">
          <span class="name-text">{{ getFileItem?.name || $t('无命名文件') }}</span>
        </jw-tooltip>
      </div>
      <!-- 操作项 -->
      <div class="preview-actions">
        <actions :activeFile="getFileItem" :isOriginalSize="isOriginalSize" :scale="scale" @cb="handleCb" @displayModeChange="handleDisplayModeChange" />
      </div>
      <span class="preview-close" @click="handleCancel"><qfei-icon-close-one-outlined /></span>
    </template>
    <div class="modal-content">
      <div class="arrow-content">
        <span class="circle" @click="handleSwitch('prev')" v-if="activeIndex > 0"><qfei-icon-left-outlined /></span>
      </div>
      <div :class="['preview-content', fileType('pdf') ? 'preview-item-pdf' : '']">
        <jw-spin :loading="loading && fileType('img')" class="loading-content">
          <div :class="[fileType('video') ? 'preview-item-video' : fileType('img') ? 'preview-item-content' : 'preview-pdf-content']">
            <!-- 图片 -->
            <template v-if="fileType('img')">
              <div class="img-container" ref="imgContainerRef">
                <img
                  v-if="fileUrl"
                  :ref="(el) => setImgRefs(el)"
                  :src="fileUrl"
                  :draggable="isDraggable"
                  class="preview-item-img"
                  :style="{
                    transform: `rotate(${rotateAngle}deg)`,
                    width: `${imgSize.width}px`,
                    height: `${imgSize.height}px`
                  }"
                  @load="handleImgLoad"
                  @mousedown="handleImgMousedown"
                />
              </div>
            </template>
            <!-- 视频 -->
            <div class="preview-item" :title="getFileItem?.name" v-else-if="fileType('video')">
              <span class="video-cover" v-if="!getFileItem.play" @click="playVideo()"><qfei-icon-caret-right-outlined /></span>
              <video ref="videoRef" class="flex preview-video" @pause="handleVideoPause" @ended="handleVideoEnded" @play="handleVideoPlay" :src="fileUrl" controls></video>
            </div>
            <!-- pdf -->
            <div class="preview-item" v-else-if="fileType('pdf')">
              <pdf :activeFile="getFileItem" :scale="scale" :pdfStatus="pdfStatus" ref="pdfRef" />
            </div>
            <template v-else>
              <!-- 文件 -->
              <div :title="getFileItem?.name" class="preview-item preview-unable-item">
                <qf-icon-excel v-if="fileType('xlsx')" class="file-icon" />
                <qf-icon-word v-else-if="fileType('docs')" class="file-icon" />
                <qf-icon-zip v-else-if="fileType('zip')" class="file-icon" />
                <qf-icon-ppt v-else-if="fileType('ppt')" class="file-icon" />
                <!-- 不可预览的视频类型 -->
                <qf-icon-video v-else-if="fileType('unableVideo')" class="file-icon" />
                <!-- 其他不可预览类型 -->
                <img :src="unable" v-else class="preview-unable-img" />

                <span class="preview-unable-text">该文件类型无法预览</span>
              </div>
            </template>

            <!-- 缩放比例 -->
            <div class="viewer-tooltip" v-if="scaleShow">{{ getScale }}%</div>
          </div>
        </jw-spin>
      </div>
      <div class="arrow-content">
        <span class="circle" @click="handleSwitch('next')" v-if="activeIndex < getFileList?.length - 1"><qfei-icon-right-outlined /></span>
      </div>
    </div>
  </jw-modal>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, reactive, nextTick } from 'vue';
import Pdf from '@/components/upload/components/pdf/index.vue';
import actions from '@/components/upload/components/actions/index.vue';
import iconUpload from '@/components/upload/components/icon/index.vue';
import { QfeiIconLeftOutlined, QfeiIconRightOutlined, QfeiIconCloseOneOutlined, QfeiIconCaretRightOutlined } from '@qfei-design/icons-vue';
import unable from '@/assets/images/upload/unable.svg';
import useXlUpload from '@/hooks/upload';
const { setUrl, getFileType, getSignedUrl } = useXlUpload();
import useLoading from '@/hooks/loading';
const { loading, setLoading } = useLoading(false);
const pdfRef = ref(null);
const props = defineProps({
  activeFile: {
    //当前文件
    type: Object,
    default: () => {}
  },
  index: {
    type: Number,
    default: 0
  },
  list: {
    type: Array,
    default: () => []
  }
});
const imgContainerRef = ref(null);
const containerSize = ref({
  width: 0,
  height: 0
});
const videoRef = ref(null);
const visible = ref(false);
const scale = ref(1);
const pdfStatus = ref(null); //pdf加载状态
const activeIndex = ref(props.index);
const scaleShow = ref(false);
// 显示模式状态：true为原始尺寸，false为适应页面
const isOriginalSize = ref(true);
// 新增：拖拽相关变量
const isDragging = ref(false); // 是否正在拖拽
const startPos = ref({ x: 0, y: 0 }); // 拖拽起始鼠标位置
const dragOffset = ref({ x: 0, y: 0 }); // 图片容器拖拽偏移量（相对于中心位置）

// 计算属性：控制图片是否可拖拽（仅当图片大于容器时可拖拽）
const isDraggable = computed(() => {
  if (!imgSize.value.width || !imgSize.value.height) return false;

  const scaledImgWidth = imgSize.value.width * scale.value;
  const scaledImgHeight = imgSize.value.height * scale.value;
  const containerEl = imgContainerRef.value?.parentElement;

  if (!containerEl) return false;

  return scaledImgWidth > containerEl.clientWidth || scaledImgHeight > containerEl.clientHeight;
});
// 所有文件
const getFileList = computed(() => {
  return props.list;
});
// 当前文件
const getFileItem = computed(() => {
  return getFileList.value[activeIndex.value];
});

// 缩放比例
const getScale = computed(() => {
  return Math.round(scale.value * 100);
});
const fileUrl = ref(null);
//获取链接
const getUrl = async () => {
  if (fileType('video') || fileType('img')) {
    const filePath = getFileItem.value?.filePath || null;
    if (filePath) {
      setLoading(true);

      const signedUrl = await getSignedUrl(filePath);
      setLoading(false);
      return signedUrl || setUrl(getFileItem.value, 1);
    } else {
      return setUrl(getFileItem.value, 1);
    }
  }
};
// 文件类型判断
const fileType = (type) => {
  return getFileType(getFileItem.value?.name || '', type);
};
const imgRef = reactive({});
// 图片引用
const setImgRefs = async (el) => {
  if (!el || !fileUrl.value) return;
  imgRef[fileUrl.value] = el;
  // 当图片元素被引用时，立即尝试获取尺寸
  if (el.complete) {
    imgSize.value = {
      width: el.naturalWidth,
      height: el.naturalHeight
    };
    updateImgCenter();
  }
};
//图片原始尺寸
const imgSize = ref({ width: 0, height: 0 });

// 图片加载完成时更新尺寸和居中
const handleImgLoad = (event) => {
  const imgEl = event.target;
  nextTick(() => {
    // 直接从事件目标获取自然尺寸，更可靠
    imgSize.value = {
      width: imgEl.naturalWidth || imgEl.width,
      height: imgEl.naturalHeight || imgEl.height
    };

    // 如果是适应页面模式，计算合适的缩放比例
    if (!isOriginalSize.value) {
      const containerEl = imgContainerRef.value?.parentElement;
      if (containerEl && imgSize.value.width > 0 && imgSize.value.height > 0) {
        const containerWidth = containerEl.clientWidth;
        const containerHeight = containerEl.clientHeight;

        // 计算宽高缩放比例，取较小值以确保图片完全显示
        const widthRatio = containerWidth / imgSize.value.width;
        const heightRatio = containerHeight / imgSize.value.height;
        scale.value = Math.min(widthRatio, heightRatio, 1); // 最大不超过1倍
      }
    }

    updateImgCenter(); // 加载完成后立即居中
  });
};
// 鼠标按下（开始拖拽）- 只有图片大于容器时才允许拖拽
const handleImgMousedown = (e) => {
  // 只允许左键拖拽
  if (e.button !== 0) return;

  // 图片尺寸 ≤ 容器尺寸：禁止拖拽
  const containerEl = imgContainerRef.value?.parentElement;
  if (!containerEl || !imgSize.value.width || !imgSize.value.height) return;

  const scaledImgWidth = imgSize.value.width * scale.value;
  const scaledImgHeight = imgSize.value.height * scale.value;
  if (scaledImgWidth <= containerEl.clientWidth && scaledImgHeight <= containerEl.clientHeight) {
    return;
  }

  isDragging.value = true;
  startPos.value = {
    x: e.clientX,
    y: e.clientY
  };
  document.addEventListener('mousemove', handleImgMousemove);
  document.addEventListener('mouseup', handleImgMouseup);
  e.preventDefault();
  e.stopPropagation();
};

// 新增：鼠标移动（拖拽中）
const handleImgMousemove = (e) => {
  if (!isDragging.value || !imgContainerRef.value) return;

  // 计算鼠标移动的距离
  const moveX = e.clientX - startPos.value.x;
  const moveY = e.clientY - startPos.value.y;

  // 更新拖拽偏移量（基于之前的偏移量累加）
  dragOffset.value = {
    x: dragOffset.value.x + moveX,
    y: dragOffset.value.y + moveY
  };

  // 实时更新图片容器位置（结合缩放和偏移）
  updateImgPosition();

  // 更新起始位置为当前位置（实现连续拖拽）
  startPos.value = {
    x: e.clientX,
    y: e.clientY
  };
};

// 新增：鼠标松开（结束拖拽）
const handleImgMouseup = () => {
  isDragging.value = false;
  // 移除文档级事件（避免内存泄漏）
  document.removeEventListener('mousemove', handleImgMousemove);
  document.removeEventListener('mouseup', handleImgMouseup);
};

// 新增：更新图片容器位置（整合缩放、旋转、拖拽）
const updateImgPosition = () => {
  if (!imgContainerRef.value || !imgSize.value.width || !imgSize.value.height) return;

  const containerEl = imgContainerRef.value.parentElement;
  if (!containerEl) return;

  // 设置变换原点为中心，确保缩放和位置计算的一致性
  imgContainerRef.value.style.transformOrigin = 'center center';

  // 1. 计算关键尺寸
  const scaledImgWidth = imgSize.value.width * scale.value; // 缩放后图片宽度
  const scaledImgHeight = imgSize.value.height * scale.value; // 缩放后图片高度
  const containerWidth = containerEl.clientWidth; // 父容器可视宽度
  const containerHeight = containerEl.clientHeight; // 父容器可视高度

  // 2. 图片尺寸完全在容器内：禁止拖拽（无需拖拽，已完全显示）
  if (scaledImgWidth <= containerWidth && scaledImgHeight <= containerHeight) {
    dragOffset.value = { x: 0, y: 0 }; // 强制重置到中心

    // 使用与图片大于容器时相同的优化方法，确保一致的居中逻辑
    imgContainerRef.value.style.transform = `
      translate(-50%, -50%) 
      scale(${scale.value})
    `;

    // 重置滚动位置，确保容器滚动条在顶部
    containerEl.scrollTop = 0;
    containerEl.scrollLeft = 0;
    return;
  }

  // 3. 图片宽度未超出但高度超出容器：保持水平居中，垂直方向可拖拽
  if (scaledImgWidth <= containerWidth && scaledImgHeight > containerHeight) {
    // 水平方向强制居中
    dragOffset.value = { x: 0, y: dragOffset.value.y }; // 水平方向强制重置到中心

    // 计算垂直方向最大允许偏移量
    const maxOffsetY = (scaledImgHeight - containerHeight) / 2 / scale.value;
    const limitedOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, dragOffset.value.y));
    const effectiveOffsetY = limitedOffsetY * scale.value;

    // 应用变换，保持水平居中
    imgContainerRef.value.style.transform = `
      translate(calc(-50% + 0px), calc(-50% + ${effectiveOffsetY}px)) 
      scale(${scale.value})
    `;

    // 保存限制后的垂直偏移量
    dragOffset.value = { x: 0, y: limitedOffsetY };
    return;
  }
  // 4. 图片宽度超出但高度未超出容器：保持垂直居中，水平方向可拖拽
  if (scaledImgWidth > containerWidth && scaledImgHeight <= containerHeight) {
    // 垂直方向强制居中
    dragOffset.value = { x: dragOffset.value.x, y: 0 }; // 垂直方向强制重置到中心

    // 计算水平方向最大允许偏移量
    const maxOffsetX = (scaledImgWidth - containerWidth) / 2 / scale.value;
    const limitedOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, dragOffset.value.x));
    const effectiveOffsetX = limitedOffsetX * scale.value;

    // 应用变换，保持垂直居中
    imgContainerRef.value.style.transform = `
      translate(calc(-50% + ${effectiveOffsetX}px), calc(-50% + 0px)) 
      scale(${scale.value})
    `;

    // 保存限制后的水平偏移量
    dragOffset.value = { x: limitedOffsetX, y: 0 };
    return;
  }

  // 5. 计算最大允许的拖拽偏移量（核心边界逻辑）
  // 水平方向：左不能拖到图片右边缘 < 容器右边缘；右不能拖到图片左边缘 > 容器左边缘
  const maxOffsetX = (scaledImgWidth - containerWidth) / 2 / scale.value;
  // 垂直方向：上不能拖到图片下边缘 < 容器下边缘；下不能拖到图片上边缘 > 容器上边缘
  const maxOffsetY = (scaledImgHeight - containerHeight) / 2 / scale.value;

  // 6. 限制偏移量在 [-maxOffset, maxOffset] 范围内（禁止空白）
  const limitedOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, dragOffset.value.x));
  const limitedOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, dragOffset.value.y));

  // 5. 应用最终位置（结合中心定位、缩放、限制后的拖拽偏移）
  // 优化：将多个transform合并为一个，确保计算准确性
  // 注意：当有缩放时，translate的像素值需要乘以缩放比例的倒数才能保持正确的视觉偏移
  const effectiveOffsetX = limitedOffsetX * scale.value;
  const effectiveOffsetY = limitedOffsetY * scale.value;

  imgContainerRef.value.style.transform = `
    translate(calc(-50% + ${effectiveOffsetX}px), calc(-50% + ${effectiveOffsetY}px)) 
    scale(${scale.value})
  `;

  // 保存限制后的偏移量，避免下次拖拽时跳回原始偏移
  dragOffset.value = { x: limitedOffsetX, y: limitedOffsetY };
};

// 优化：重置拖拽偏移（切换图片/重置缩放时调用）
const resetDragOffset = () => {
  dragOffset.value = { x: 0, y: 0 };
};
const rotateAngle = ref(0);
// 处理显示模式切换
const handleDisplayModeChange = (newMode) => {
  isOriginalSize.value = newMode;

  if (fileType('img')) {
    if (newMode) {
      // 切换到原始尺寸
      scale.value = 1;
    } else {
      // 切换到适应页面，计算合适的缩放比例
      const containerEl = imgContainerRef.value?.parentElement;
      if (containerEl && imgSize.value.width > 0 && imgSize.value.height > 0) {
        const containerWidth = containerEl.clientWidth;
        const containerHeight = containerEl.clientHeight;

        // 计算宽高缩放比例，取较小值以确保图片完全显示
        const widthRatio = containerWidth / imgSize.value.width;
        const heightRatio = containerHeight / imgSize.value.height;
        const fitScale = Math.min(widthRatio, heightRatio, 1); // 最大不超过1倍

        scale.value = fitScale;
      }
    }

    // 重置位置并更新
    resetDragOffset();
    nextTick(() => {
      updateImgCenter();
      updateImgPosition();
    });
  }
};

// 操作回调
const handleCb = (type, params) => {
  if (type === 'zoom') {
    //缩放
    scale.value = params.scale;
    scaleShow.value = true;
    pdfRef.value?.handleOpCb({ type: 'zoom', scale: params.scale });
    nextTick(() => {
      updateImgPosition();
    });
  }
  if (type === 'rotate') {
    //旋转
    rotateAngle.value = params.rotateAngle;
    nextTick(() => {
      updateImgCenter();
      updateImgPosition(); // 旋转后也需校准边界
    });
  }
  if (type === 'pageChange' || type === 'pageTo') {
    // 页面改变
    pdfRef.value?.handleOpCb({ type: type, currentPage: params.currentPage });
  }
};
// 加载图片
const onLoadImg = async () => {
  // 主动创建一个临时图片对象来获取原始尺寸，避免依赖DOM引用
  const getImageUrl = fileUrl.value; // 提取变量，提升可读性
  // 前置校验：URL为空/无效时直接返回默认值
  if (!getImageUrl || typeof getImageUrl !== 'string') {
    console.error('Invalid image URL:', getImageUrl);
    imgSize.value = { width: 300, height: 200 };
    updateImgCenter();
    return;
  }

  const tempImg = new Image();

  // 先绑定所有事件
  tempImg.onload = () => {
    // 验证图片是否真的加载成功（排除异常情况）
    if (tempImg.naturalWidth === 0 || tempImg.naturalHeight === 0) {
      tempImg.onerror?.(); // 复用错误处理逻辑
      return;
    }
    imgSize.value = {
      width: tempImg.naturalWidth,
      height: tempImg.naturalHeight
    };
    updateImgCenter();
    // 解除事件绑定
    tempImg.onload = tempImg.onerror = null;
  };

  tempImg.onerror = () => {
    console.error('Failed to load image:', getImageUrl);
    // 设置默认尺寸，避免图片完全不显示
    imgSize.value = { width: 300, height: 200 };
    updateImgCenter();
    // 解除事件绑定
    tempImg.onload = tempImg.onerror = null;
  };

  // 设置src（必须在事件绑定后）
  tempImg.src = getImageUrl;

  // 处理缓存图片（此时事件已绑定，无需担心丢失）
  if (tempImg.complete) {
    // 校验图片是否真的加载成功
    if (tempImg.naturalWidth > 0) {
      imgSize.value = {
        width: tempImg.naturalWidth,
        height: tempImg.naturalHeight
      };
      updateImgCenter();
      // 清空事件，避免重复触发
      tempImg.onload = tempImg.onerror = null;
    } else {
      // complete=true但加载失败，触发错误逻辑
      tempImg.onerror();
    }
  }
};

//初始状态
const init = async () => {
  // 默认使用适应页面尺寸

  fileUrl.value = await getUrl();
  isOriginalSize.value = false;
  scale.value = 1;
  rotateAngle.value = 0;
  scaleShow.value = false;
  resetDragOffset(); // 切换图片时重置拖拽位置

  // 重置图片尺寸
  imgSize.value = { width: 0, height: 0 };

  nextTick(() => {
    if (fileType('video')) {
      updateFileStatus(false); // 同步状态为“未播放”，显示播放封面
    }

    if (fileType('img')) {
      const containerEl = imgContainerRef.value?.parentElement; // preview-item-content

      if (containerEl) {
        // 获取父容器可视区域尺寸（排除padding）
        containerSize.value = {
          width: containerEl.clientWidth,
          height: containerEl.clientHeight
        };
      }

      // 初始化时根据显示模式设置缩放比例
      if (!isOriginalSize.value && containerEl && imgSize.value.width > 0 && imgSize.value.height > 0) {
        const containerWidth = containerEl.clientWidth;
        const containerHeight = containerEl.clientHeight;

        // 计算宽高缩放比例，取较小值以确保图片完全显示
        const widthRatio = containerWidth / imgSize.value.width;
        const heightRatio = containerHeight / imgSize.value.height;
        scale.value = Math.min(widthRatio, heightRatio, 1); // 最大不超过1倍
      }

      // 强制设置容器初始缩放和居中
      if (imgContainerRef.value) {
        imgContainerRef.value.style.transform = `translate(-50%, -50%) scale(${scale.value})`;
      }

      onLoadImg();
    }
    if (fileType('pdf')) {
      pdfRef.value?.getPdfFile();
    }
  });
};

// 图片居中
const updateImgCenter = () => {
  const containerEl = imgContainerRef.value?.parentElement;
  if (!containerEl || !imgContainerRef.value || imgSize.value.width === 0) return;

  // 计算缩放后的图片尺寸
  const scaledImgWidth = imgSize.value.width * scale.value;
  const scaledImgHeight = imgSize.value.height * scale.value;
  const containerWidth = containerEl.clientWidth;
  const containerHeight = containerEl.clientHeight;

  // 检查图片是否超出父容器，且用户是否已手动拖动过
  // 如果图片超出容器且用户已拖动，则不执行居中操作，避免闪烁
  const isImgExceedContainer = scaledImgWidth > containerWidth || scaledImgHeight > containerHeight;
  const hasUserDragged = dragOffset.value.x !== 0 || dragOffset.value.y !== 0;

  if (isImgExceedContainer && hasUserDragged) {
    // 图片超出容器且用户已拖动，不执行居中操作
    return;
  }

  // 重置拖拽偏移，确保缩放后回到中心
  dragOffset.value = { x: 0, y: 0 };

  // 计算图片容器的精确位置，确保无论缩放比例多小都能正确居中
  // 使用transform-origin确保缩放中心正确
  imgContainerRef.value.style.transformOrigin = 'center center';

  // 直接设置图片容器的位置，不使用滚动
  // 优化：使用calc确保translate计算的准确性
  imgContainerRef.value.style.transform = `
    translate(-50%, -50%) 
    scale(${scale.value})
  `;

  // 重置滚动位置，确保容器滚动条在顶部
  containerEl.scrollTop = 0;
  containerEl.scrollLeft = 0;
};

// 窗口 resize 时校准边界
const handleResize = () => {
  if (fileType('img')) {
    const containerEl = imgContainerRef.value?.parentElement;
    if (containerEl) {
      containerSize.value = {
        width: containerEl.clientWidth,
        height: containerEl.clientHeight
      };
      imgContainerRef.value.style.transform = `translate(-50%, -50%) scale(${scale.value})`;
      updateImgCenter();
      updateImgPosition(); // 窗口变化后校准拖拽边界
    }
  }
};

// 处理键盘事件，支持左右箭头键切换图片
const handleKeydown = (event) => {
  // 仅在模态框可见时处理键盘事件
  if (!visible.value) return;

  // 左右箭头键切换图片
  if (event.key === 'ArrowLeft') {
    event.preventDefault(); // 阻止默认行为（如页面滚动）
    if (activeIndex.value > 0) {
      handleSwitch('prev');
    }
  } else if (event.key === 'ArrowRight') {
    event.preventDefault(); // 阻止默认行为（如页面滚动）
    if (activeIndex.value < getFileList.value.length - 1) {
      handleSwitch('next');
    }
  }
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  // 添加键盘事件监听
  window.addEventListener('keydown', handleKeydown);
});

// 视频更新播放状态
const updateFileStatus = (play) => {
  const currentFile = getFileList.value[activeIndex.value];
  if (currentFile) {
    currentFile.play = play; // 重置状态
  }
};

const handleCancel = () => {
  visible.value = false;
};

const openPreviewModal = (params) => {
  fileUrl.value = null;
  visible.value = true;
  activeIndex.value = params.index;
  init();
};
/**
 * 通用切换方法
 * @param {string} direction - 切换方向：'prev'（上一张）、'next'（下一张）
 */
const handleSwitch = (direction) => {
  // 2. 计算目标索引
  const targetIndex = direction == 'prev' ? activeIndex.value - 1 : activeIndex.value + 1;

  // 3. 校验目标索引合法性（避免超出数组边界）
  const maxIndex = getFileList.value.length - 1;
  if (targetIndex < 0 || targetIndex > maxIndex) return;
  fileUrl.value = null;
  activeIndex.value = targetIndex;

  init();
};

//播放视频
const playVideo = () => {
  const fileItem = getFileList.value[activeIndex.value];
  fileItem.play = true;
  videoRef.value.play().catch((err) => {
    fileItem.play = false;
    console.log('播放失败（可能是浏览器拦截自动播放）：', err);
  });
};
// 视频暂停
const handleVideoPause = () => {
  updateFileStatus(false);
};
// 视频播放
const handleVideoPlay = () => {
  updateFileStatus(true);
};
// 视频播放完毕
const handleVideoEnded = () => {
  if (fileType('video')) {
    updateFileStatus(false); // 同步状态为“未播放”，显示播放封面

    if (videoRef.value) {
      videoRef.value.currentTime = 0;
    }
  }
};

//组件卸载时清理视频
onUnmounted(() => {
  // 视频
  if (videoRef.value) {
    videoRef.value.pause();
    videoRef.value.removeEventListener('pause', handleVideoPause);
    videoRef.value.removeEventListener('ended', handleVideoEnded);
  }
  window.removeEventListener('resize', handleResize);
  // 移除键盘事件监听
  window.removeEventListener('keydown', handleKeydown);
});

defineExpose({ openPreviewModal });
</script>
<style lang="less">
@import './index.less';
</style>
